import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { format, subDays } from "date-fns";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { registerDirectApplyIntegrationDataHook } from "../../hooks/integrationDataHooks";
import { registerDirectApplyTableDataHook } from "../../hooks/tableDataHooks";
import { useApplySyncComparison } from "../../sync/api/useApplySyncComparison";

// Side-effect: register resyncButton custom component for schema renderer
import "../../sync/components/register";
import type { ModuleDataValue } from "@/features/integrations/shared/renderer/hooks/types";

registerDirectApplyIntegrationDataHook("intermediair", function useIntermediairDirectApplyIntegrationData(params) {
  const { integrationName, clientId } = params;
  const [searchParams] = useSearchParams();

  const defaultFrom = format(subDays(new Date(), 7), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const from = searchParams.get("from") || defaultFrom;
  const to = searchParams.get("to") || defaultTo;

  const queryResult = useApplySyncComparison(integrationName, clientId, from, to);

  const transformedData = useMemo(() => {
    const comparison = queryResult.data;
    if (!comparison) return undefined;

    return {
      availableCount: comparison.availableCount,
      fetchedCount: comparison.fetchedCount,
      missingCount: comparison.statusCounts?.missing ?? 0,
      partnerUnavailable: !comparison.partnerDataAvailable,
      partnerError: comparison.partnerError,
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<Record<string, ModuleDataValue>, AxiosError>;
});

registerDirectApplyTableDataHook("intermediair", (clientId) =>
  clientId ? useIntermediairDirectApplyTableData : undefined
);

function useIntermediairDirectApplyTableData(params: {
  clientId: string | undefined;
  tenantId: string | undefined;
  page: number;
  pageSize: number;
}) {
  const { clientId, page, pageSize } = params;
  const [searchParams] = useSearchParams();

  const defaultFrom = format(subDays(new Date(), 7), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const from = searchParams.get("from") || defaultFrom;
  const to = searchParams.get("to") || defaultTo;
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "all";
  const orderBy = searchParams.get("orderBy") || "";
  const sortDesc = searchParams.get("descending") === "true";

  const queryResult = useApplySyncComparison("intermediair", clientId, from, to);

  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;

    let items = queryResult.data.applications ?? [];

    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (a) =>
          a.applicationId.toLowerCase().includes(q) ||
          a.vacancyReference?.toLowerCase().includes(q),
      );
    }

    if (statusFilter && statusFilter !== "all") {
      items = items.filter((a) => a.syncStatus === statusFilter);
    }

    if (orderBy) {
      items = [...items].sort((a, b) => {
        const aVal = String((a as unknown as Record<string, unknown>)[orderBy] ?? "");
        const bVal = String((b as unknown as Record<string, unknown>)[orderBy] ?? "");
        const cmp = aVal.localeCompare(bVal);
        return sortDesc ? -cmp : cmp;
      });
    }

    const start = (page - 1) * pageSize;
    const paged = items.slice(start, start + pageSize);

    return {
      data: paged as unknown[],
      totalRecords: items.length,
    };
  }, [queryResult.data, search, statusFilter, orderBy, sortDesc, page, pageSize]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError>;
}
